import {INTER_PILL_SPACER} from './constants'
import {textareaWithVariables_isPillElement} from './is-element'
import {textareaWithVariables_isSpacer} from './is-spacer'


function isBreak(node: Node | null): boolean {
    return !!node
        && node.nodeType === Node.ELEMENT_NODE
        && (node as Element).tagName === 'BR'
}

// A `<div>`/`<p>` child is a separate line context (browsers wrap Enter
// in blocks). Treated as a line edge for a pill that sits next to one.
function isBlock(node: Node | null): boolean {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false
    const tag = (node as Element).tagName
    return tag === 'DIV' || tag === 'P'
}

function spacer(): Text {
    return document.createTextNode(INTER_PILL_SPACER)
}


// Idempotent. Ensures every pill has an editable text node on any side
// that faces a LINE EDGE, so the caret there renders at the normal font
// height (not the tall line-box height) and vertical arrow navigation
// has a real text position to work from. We already needed this between
// two pills; line edges need it for the exact same reason.
//
// Strategy: wipe all spacers at this level, then rebuild. A pill gets a
// LEADING spacer when its left neighbour is the container start, a
// `<br>`, a block, or another pill; a TRAILING spacer when its right
// neighbour is the container end, a `<br>`, or a block. Two adjacent
// pills share one spacer.
//
// Recurses into `<div>`/`<p>` blocks and treats each block's own
// start/end as a line edge.
//
// IMPORTANT: this removes and re-adds spacer text nodes, so a caret left
// sitting inside a wiped spacer would be lost. Every caller that has a
// live caret MUST set the caret AFTER calling this (see tokenize-at-caret,
// paste-text, insertToken — they place the caret past the trailing spacer).
export function textareaWithVariables_normalizePillSpacers(root: Node): void {

    // Pass A — strip every existing spacer at this level.
    for (const node of Array.from(root.childNodes)) {
        if (textareaWithVariables_isSpacer(node)) root.removeChild(node)
    }

    // Pass B — leading spacers (this also fills the gap between two pills).
    for (const node of Array.from(root.childNodes)) {
        if (!textareaWithVariables_isPillElement(node)) continue
        const left = node.previousSibling
        if (left === null || isBreak(left) || isBlock(left) || textareaWithVariables_isPillElement(left)) {
            root.insertBefore(spacer(), node)
        }
    }

    // Pass C — trailing spacers, only at a true line end. A pill followed
    // by real text (or by the shared spacer before the next pill) is fine.
    for (const node of Array.from(root.childNodes)) {
        if (!textareaWithVariables_isPillElement(node)) continue
        const right = node.nextSibling
        if (right === null || isBreak(right) || isBlock(right)) {
            root.insertBefore(spacer(), right)   // insertBefore(.., null) appends
        }
    }

    // Recurse into block children — each is its own line context.
    for (const node of Array.from(root.childNodes)) {
        if (isBlock(node)) textareaWithVariables_normalizePillSpacers(node)
    }
}
